<script setup lang="ts">
import { onMounted, ref } from "vue"
import { RouterLink } from "vue-router"

import { SidebarTrigger } from "@/components/ui/sidebar"
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Bell, Sun, Moon } from "lucide-vue-next"

type Crumb = {
  label: string
  name?: string
  to?: any
  params?: Record<string, any>
  query?: Record<string, any>
}

const props = withDefaults(defineProps<{
  /** Breadcrumbs da página atual */
  breadcrumb?: Crumb[]
  /** Título/descrição da seção de page header (opcional) */
  title?: string
  description?: string
  /** Exibir seção de page header (título/descrição + ações) */
  showPageHeader?: boolean
}>(), {
  breadcrumb: () => [],
  title: "Página",
  description: "",
  showPageHeader: true,
})

/** Tema com persistência */
const isDark = ref(false)
function applyTheme(dark: boolean) {
  document.documentElement.classList.toggle("dark", dark)
  document.documentElement.style.colorScheme = dark ? "dark" : "light"
}
function toggleTheme() {
  isDark.value = !isDark.value
  applyTheme(isDark.value)
  try { localStorage.setItem("prefers-dark", String(isDark.value)) } catch {}
}
onMounted(() => {
  try {
    const saved = localStorage.getItem("prefers-dark")
    isDark.value = saved ? saved === "true" : document.documentElement.classList.contains("dark")
    applyTheme(isDark.value)
  } catch {}
})
</script>

<template>
  <!-- Header superior -->
  <header
      class="flex h-14 shrink-0 items-center gap-2 px-4 border-b
           transition-[width,height] ease-linear
           group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12"
  >
    <SidebarTrigger class="-ml-1" />

    <!-- Breadcrumb -->
    <nav class="flex-1" aria-label="Breadcrumb">
      <Breadcrumb>
        <BreadcrumbList>
          <template v-if="(breadcrumb?.length || 0) > 0">
            <template v-for="(bc, i) in breadcrumb" :key="`${bc.label}-${i}`">
              <BreadcrumbItem v-if="i < (breadcrumb?.length || 0) - 1" class="hidden md:block">
                <RouterLink
                    v-if="bc.name || bc.to"
                    :to="bc.name ? { name: bc.name, params: bc.params, query: bc.query } : (bc.to || '#')"
                    class="text-muted-foreground hover:text-foreground"
                >
                  {{ bc.label }}
                </RouterLink>
                <span v-else class="text-muted-foreground">{{ bc.label }}</span>
              </BreadcrumbItem>
              <BreadcrumbSeparator v-if="i < (breadcrumb?.length || 0) - 1" class="hidden md:block" />
              <BreadcrumbItem v-else>
                <BreadcrumbPage>{{ bc.label }}</BreadcrumbPage>
              </BreadcrumbItem>
            </template>
          </template>
          <template v-else>
            <BreadcrumbItem><BreadcrumbPage>{{ title || 'Página' }}</BreadcrumbPage></BreadcrumbItem>
          </template>
        </BreadcrumbList>
      </Breadcrumb>
    </nav>

    <!-- 1) Slot de ações inline (prioridade máxima) -->
    <slot name="header-right" />

    <!-- 2) Teleport opcional: páginas podem enviar ações aqui -->
    <div id="header-actions" />

    <!-- 3) Ações fixas (notificações + tema); sobrescreva com slot se quiser -->
    <slot name="header-fixed-right">
      <div class="flex items-center gap-2">
        <Button variant="ghost" size="icon" class="h-8 w-8" aria-label="Notificações">
          <Bell class="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" class="h-8 w-8" @click="toggleTheme" aria-label="Alternar tema">
          <Sun v-if="!isDark" class="h-4 w-4" />
          <Moon v-else class="h-4 w-4" />
        </Button>
      </div>
    </slot>
  </header>

  <!-- Page header (opcional) -->
  <section
      v-if="showPageHeader"
      class="flex min-h-20 flex-col gap-3 bg-background px-4
           md:min-h-25 md:flex-row md:items-center md:justify-between"
  >
    <div class="min-w-0">
      <h1 class="truncate text-2xl font-bold tracking-tight">
        {{ title }}
      </h1>
      <p v-if="description" class="mt-1 text-sm text-muted-foreground">
        {{ description }}
      </p>
    </div>

    <div class="flex items-center gap-2">
      <!-- Slot explícito para ações do page header -->
      <slot name="page-header-right" />

      <!-- Teleport opcional para ações do page header -->
      <div class="page-header-actions" />
    </div>
  </section>
</template>
